import React from 'react';
import { Platform, StyleSheet, Text, View, Pressable } from 'react-native';
import { FontFamily } from '../../../constants/fonts';
import { useTheme } from '../../../theme';
import SkeletonBlock from './SkeletonBlock';
import { HeatMapData } from './types';

interface HeatMapSectionProps {
  title: string;
  note: string;
  data: HeatMapData;
  legendPalette: [string, string, string];
  cellPalette: [string, string, string, string, string];
  /** When true the heat-map grid is replaced by a shimmering placeholder. */
  loading?: boolean;
  /** Optional error message to display inside the card when the request failed. */
  error?: string | null;
  /** When true, shows the filter-hint empty message instead of the generic one. */
  hasActiveFilters?: boolean;
}

const DEFAULT_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const HOURS_PER_DAY = 24;

/**
 * Loading placeholder for the heat-map grid. Renders the exact final geometry
 * (one row per day × 24 hour cells) as shimmering cells so the card keeps its
 * height and the day / hour axis labels stay aligned.
 */
const HeatMapSkeleton: React.FC<{ dayCount: number }> = ({ dayCount }) => (
  <View style={styles.gridCol}>
    {Array.from({ length: dayCount }).map((_, rowIndex) => (
      <View key={`skeleton-row-${rowIndex}`} style={styles.gridRow}>
        {Array.from({ length: HOURS_PER_DAY }).map((__, colIndex) => (
          <SkeletonBlock key={`skeleton-cell-${rowIndex}-${colIndex}`} style={styles.skeletonCell} />
        ))}
      </View>
    ))}
  </View>
);

const HeatMapSection: React.FC<HeatMapSectionProps> = ({ title, note, data, legendPalette, cellPalette, loading = false, error = null, hasActiveFilters = false }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';
  const days = data.days.length ? data.days : DEFAULT_DAYS;

  const isEmptyMatrix = !data.matrix || data.matrix.length === 0 || data.matrix.every(row => row.every((v) => (v ?? 0) === 0));

  const [tooltipVisible, setTooltipVisible] = React.useState(false);
  const [tooltipText, setTooltipText] = React.useState<string | null>(null);
  const [tooltipPagePos, setTooltipPagePos] = React.useState<{ x: number; y: number } | null>(null);

  const positionFromEvent = (e: any) => {
    try {
      const ne = e?.nativeEvent as any;
      if (ne && typeof ne.clientX === 'number' && typeof ne.clientY === 'number') {
        setTooltipPagePos({ x: ne.clientX, y: ne.clientY });
      }
    } catch (err) {
      // ignore
    }
  };

  const cellColor = (value: number) => {
    // Match mock HTML bucket thresholds for heat-map intensity.
    const idx = value <= 0 ? 0 : value < 3 ? 1 : value < 5 ? 2 : value < 8 ? 3 : 4;
    return cellPalette[idx];
  };

  return (
    <View style={[styles.card, isDark && { backgroundColor: colors.background.surface, borderColor: colors.border.default }]}>
      <View style={styles.cardHead}>
        <Text style={[styles.cardTitle, isDark && { color: colors.text.primary }]}>{title}</Text>
        <View style={styles.legend}>
          <Text style={[styles.legendText, isDark && { color: colors.text.secondary }]}>Fewer</Text>
          <View style={[styles.swatch, { backgroundColor: legendPalette[0] }]} />
          <View style={[styles.swatch, { backgroundColor: legendPalette[1] }]} />
          <View style={[styles.swatch, { backgroundColor: legendPalette[2] }]} />
          <Text style={[styles.legendText, isDark && { color: colors.text.secondary }]}>More</Text>
        </View>
      </View>

      <View style={styles.hmWrap}>
        {isEmptyMatrix ? (
          <View style={styles.emptyFullWrap}>
            <Text style={[styles.noDataText, isDark && { color: colors.text.secondary }]}>{'Try expanding your date range or removing some filters.'}</Text>
          </View>
        ) : (
          <>
            <View style={styles.daysCol}>
              {days.map((day) => (
                <Text key={day} style={[styles.dayText, isDark && { color: colors.text.secondary }]}>{day}</Text>
              ))}
            </View>
            {loading ? (
              <HeatMapSkeleton dayCount={days.length} />
            ) : error ? (
              <View style={styles.errorWrap}>
                <Text style={styles.errorText}>{error}</Text>
              </View>
            ) : (
              <View style={styles.gridCol}>
                {data.matrix.map((row, rowIndex) => (
                  <View key={`row-${rowIndex}`} style={styles.gridRow}>
                    {row.map((value, colIndex) => {
                      const hour = colIndex % HOURS_PER_DAY;
                      const hourLabel = (() => {
                        const h = hour % 24;
                        const ampm = h < 12 ? 'AM' : 'PM';
                        const display = h % 12 === 0 ? 12 : h % 12;
                        return `${display} ${ampm}`;
                      })();

                      return (
                        <Pressable
                          key={`cell-${rowIndex}-${colIndex}`}
                          style={[styles.cell, { backgroundColor: cellColor(value) }]}
                          onHoverIn={(e:any) => {
                            positionFromEvent(e);
                            setTooltipText(`${days[rowIndex]} ${hourLabel} - ${value} ticket${value === 1 ? '' : 's'}`);
                            setTooltipVisible(true);
                          }}
                          onHoverOut={() => { setTooltipVisible(false); setTooltipPagePos(null); }}
                          onPressIn={(e:any) => {
                            positionFromEvent(e);
                            setTooltipText(`${days[rowIndex]} ${hourLabel} - ${value} ticket${value === 1 ? '' : 's'}`);
                            setTooltipVisible(true);
                          }}
                          onPressOut={() => { setTooltipVisible(false); setTooltipPagePos(null); }}
                        />
                      );
                    })}
                  </View>
                ))}
              </View>
            )}
          </>
        )}
      </View>

      <View style={styles.xLabelsWrap}>
        <View style={styles.xSpacer} />
        <View style={styles.xLabels}>
          <Text style={[styles.xLabelText, isDark && { color: colors.text.secondary }]}>12 AM</Text>
          <Text style={[styles.xLabelText, isDark && { color: colors.text.secondary }]}>4 AM</Text>
          <Text style={[styles.xLabelText, isDark && { color: colors.text.secondary }]}>8 AM</Text>
          <Text style={[styles.xLabelText, isDark && { color: colors.text.secondary }]}>12 PM</Text>
          <Text style={[styles.xLabelText, isDark && { color: colors.text.secondary }]}>4 PM</Text>
          <Text style={[styles.xLabelText, isDark && { color: colors.text.secondary }]}>8 PM</Text>
          <Text style={[styles.xLabelText, isDark && { color: colors.text.secondary }]}>12 AM</Text>
        </View>
      </View>

      <Text style={[styles.cardNote, isDark && { color: colors.text.secondary }]}>{note}</Text>
      {tooltipVisible && tooltipText ? (
        (() => {
          const page = tooltipPagePos;
          if (page && typeof page.x === 'number' && typeof page.y === 'number') {
            const padding = 8;
            const offsetX = 12;
            const offsetY = 36;
            const winW = typeof window !== 'undefined' ? window.innerWidth : 0;
            const winH = typeof window !== 'undefined' ? window.innerHeight : 0;
            const tooltipWidthEstimate = 220;
            let left = page.x + offsetX;
            if (winW) left = Math.min(left, Math.max(padding, winW - tooltipWidthEstimate - padding));
            let top = page.y - offsetY;
            if (winH) top = Math.min(Math.max(padding, top), winH - padding);

            return (
              <View pointerEvents="none" style={[styles.tooltipOverlay, { position: 'fixed', left, top, zIndex: 1000 }]}> 
                <View style={styles.tooltipBubble}>
                  <Text style={styles.tooltipText}>{tooltipText}</Text>
                </View>
              </View>
            );
          }

          return (
            <View style={styles.tooltipOverlay} pointerEvents="none">
              <View style={styles.tooltipBubble}>
                <Text style={styles.tooltipText}>{tooltipText}</Text>
              </View>
            </View>
          );
        })()
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E8EDF3',
    padding: 20,
    marginBottom: 16,
    flexGrow: 1,
    minWidth: 380,
    flexBasis: 0,
  },
  cardHead: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
    gap: 10,
    flexWrap: 'wrap',
  },
  cardTitle: {
    fontSize: 16,
    fontFamily: FontFamily.bold,
    color: '#2D2F33',
  },
  legend: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  legendText: {
    fontSize: 11,
    fontFamily: FontFamily.regular,
    color: '#7A8088',
  },
  swatch: {
    width: 14,
    height: 14,
    borderRadius: 3,
  },
  hmWrap: {
    flexDirection: 'row',
    gap: 6,
  },
  daysCol: {
    width: 44,
    gap: 3,
  },
  dayText: {
    height: 22,
    textAlign: 'right',
    fontSize: 11,
    fontFamily: FontFamily.medium,
    color: '#616161',
    lineHeight: 22,
  },
  gridCol: {
    flex: 1,
    gap: 3,
  },
  gridRow: {
    flexDirection: 'row',
    gap: 3,
  },
  cell: {
    flex: 1,
    height: 22,
    borderRadius: 3,
  },
  skeletonCell: {
    flex: 1,
    height: 22,
    borderRadius: 3,
  },
  xLabelsWrap: {
    flexDirection: 'row',
    marginTop: 6,
  },
  xSpacer: {
    width: 44,
  },
  xLabels: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  xLabelText: {
    fontSize: 10,
    fontFamily: FontFamily.regular,
    color: '#9AA1AB',
  },
  cardNote: {
    fontSize: 12,
    fontFamily: FontFamily.regular,
    color: '#7A8088',
    marginTop: 6,
  },
  emptyFullWrap: {
    flex: 1,
    minHeight: 160,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorWrap: {
    minHeight: 160,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    color: '#B42318',
    fontSize: 13,
    fontFamily: FontFamily.medium,
    textAlign: 'center',
  },
  noDataText: {
    color: '#7A8088',
    fontSize: 13,
    fontFamily: FontFamily.medium,
    textAlign: 'center',
  },
  tooltipOverlay: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: [{ translateX: -50 }, { translateY: -50 }],
    zIndex: 1000,
  },
  tooltipBubble: {
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    borderRadius: 4,
    padding: 8,
  },
  tooltipText: {
    color: '#FFF',
    fontSize: 12,
    fontFamily: FontFamily.medium,
  },
});

export default HeatMapSection;






